import { BUSINESS_TYPES } from "./constants";

export async function lookupByName(query, supabase) {
  if (!query || query.trim().length < 2) return [];

  const q = query.trim();

  const { data, error } = await supabase
    .from(process.env.REACT_APP_DB)
    .select("t, nm, bn")
    .or(`nm.ilike.%${q}%,bn.ilike.%${q}%`)
    .limit(25);

  if (error) {
    console.error("Name lookup failed:", error);
    return [];
  }

  return data || [];
}

export async function lookupNTN(raw, supabase) {
  if (!raw?.trim()) return null;

  const wasSntn = /^S\d/i.test(raw.trim());
  const clean = stripSntn(raw);
  const base = clean.split("-")[0];
  const enteredCheck = clean.includes("-") ? clean.split("-")[1] : null;

  // Try exact match first
  const { data: exactData, error: exactError } = await supabase
    .from(process.env.REACT_APP_DB)
    .select("t, nm, bn")
    .eq("t", clean)
    .maybeSingle();

  if (exactError) console.error("NTN exact lookup failed:", exactError);

  let record = exactData || null;
  let matchType = "exact";

  if (!record) {
    // Try base match (7-digit prefix)
    const { data: baseData, error: baseError } = await supabase
      .from(process.env.REACT_APP_DB)
      .select("t, nm, bn")
      .like("t", `${base}%`)
      .limit(1)
      .maybeSingle();

    if (baseError) console.error("NTN base lookup failed:", baseError);

    record = baseData || null;
    matchType = record ? "base" : null;
  }

  if (!record) return { found: false, raw, wasSntn, clean };

  const storedCheck = record.t.includes("-") ? record.t.split("-")[1] : null;

  const checkMismatch =
    enteredCheck !== null &&
    storedCheck !== null &&
    enteredCheck !== storedCheck;

  return {
    found: true,
    raw,
    wasSntn,
    clean,
    record,
    matchType,
    enteredCheck,
    storedCheck,
    checkMismatch,
  };
}

// ─── NTN / REGISTRATION UTILITIES ────────────────────────────────────────────
// Strip SNTN "S" prefix and normalise to plain NTN string
export function stripSntn(raw) {
  const v = raw.trim().toUpperCase();
  return /^S\d/.test(v) ? v.slice(1) : v;
}

// ─── OTHER REGISTRATION FORMATS (format-only, no check digit) ─────────────
const STRN_REGEX = /^\d{13}$/;
const PRA_REGEX = /^PRA\d{10}$/i;
const KPRA_REGEX = /^KPRA\d{8,10}$/i;
const BRA_REGEX = /^BRA\d{8,10}$/i;

export function validateSTRN(raw) {
  const v = raw.trim().toUpperCase().replace(/\s/g, "");
  if (!v) return { valid: false, reason: "" };
  if (STRN_REGEX.test(v))
    return { valid: true, reason: "Valid FBR STRN format (13 digits)." };
  if (PRA_REGEX.test(v))
    return {
      valid: true,
      reason: "Valid PRA (Punjab Revenue Authority) registration.",
    };
  if (KPRA_REGEX.test(v))
    return {
      valid: true,
      reason: "Valid KPRA (KPK Revenue Authority) registration.",
    };
  if (BRA_REGEX.test(v))
    return {
      valid: true,
      reason: "Valid BRA (Balochistan Revenue Authority) registration.",
    };
  return {
    valid: false,
    reason:
      "Format not recognised. STRN = 13 digits · PRA starts with 'PRA' · KPRA starts with 'KPRA' · BRA starts with 'BRA'",
  };
}

// ─── FBR / OCR UTILITIES ──────────────────────────────────────────────────────
export function buildFBRLink(ntn, strn) {
  if (ntn)
    return `https://e.fbr.gov.pk/esbn/Verification?ntn=${encodeURIComponent(ntn)}`;
  if (strn)
    return `https://e.fbr.gov.pk/esbn/Verification?strn=${encodeURIComponent(strn)}`;
  return "https://e.fbr.gov.pk/esbn/Verification";
}
export function buildATLLink(ntn) {
  return ntn
    ? `https://e.fbr.gov.pk/esbn/ATL?ntn=${encodeURIComponent(ntn)}`
    : "https://e.fbr.gov.pk";
}

export function analyseFBRQR(qrData) {
  if (!qrData) return null;
  const isFBRPOS = /e\.fbr\.gov\.pk|fbr\.gov\.pk\/pos|POSID|InvNum/i.test(
    qrData,
  );
  const isProvincial =
    /srb\.gos\.pk|pra\.punjab\.gov\.pk|kpra\.gov\.pk|bra\.gob\.pk/i.test(
      qrData,
    );
  const extractedNTN = qrData.match(/\b(\d{7}-\d)\b/)?.[1] || null;
  const extractedSTRN = qrData.match(/\b(\d{13})\b/)?.[1] || null;
  const posInvoiceNum = qrData.match(/InvNum[=:](\d+)/i)?.[1] || null;
  return {
    isFBRPOS,
    isProvincial,
    extractedNTN,
    extractedSTRN,
    posInvoiceNum,
    raw: qrData,
  };
}

export function extractFromText(text) {
  const ntnMatch = text.match(/\b(\d{7}-\d)\b/);
  const strnMatch = text.match(/\b(\d{13})\b/);
  const sntnMatch = text.match(/\b(S\d{7}-\d)\b/i);
  const praMatch = text.match(/\b(PRA\d{9,11})\b/i);
  const kpraMatch = text.match(/\b(KPRA\d{7,10})\b/i);
  const braMatch = text.match(/\b(BRA\d{7,10})\b/i);
  const taxLines = [];
  const taxRegex =
    /(GST|SST|Service\s*Tax|Sales\s*Tax|Sindh\s*Sales\s*Tax|FED|Federal\s*Excise|Withholding\s*Tax|WHT)[^\n\r]{0,40}(Rs\.?|PKR|₨)\s*[\d,]+\.?\d*/gi;
  let m;
  while ((m = taxRegex.exec(text)) !== null) taxLines.push(m[0].trim());
  const totalRegex = /total[^\n\r]{0,20}(Rs\.?|PKR|₨)\s*([\d,]+\.?\d*)/gi;
  let total = null;
  while ((m = totalRegex.exec(text)) !== null) total = m[2].replace(/,/g, "");
  return {
    ntn:
      sntnMatch?.[1] ||
      ntnMatch?.[1] ||
      praMatch?.[1] ||
      kpraMatch?.[1] ||
      braMatch?.[1] ||
      null,
    strn: strnMatch?.[1] || null,
    sntn: sntnMatch?.[1] || null,
    taxLines,
    total,
    hasTaxClaimed:
      taxLines.length > 0 ||
      /\b(NTN|STRN|SNTN|GST|Tax Invoice|Reg\.?\s*No)\b/i.test(text),
  };
}

// ─── SIGNAL SCORING ───────────────────────────────────────────────────────────
export function scoreSignals({
  ntnLookup,
  strnResult,
  hasQR,
  qrAnalysis,
  hasTaxClaimed,
  ocrNTNLookup,
  businessType,
}) {
  let score = 0;
  const signals = [];
  const bt = BUSINESS_TYPES.find((b) => b.id === businessType);

  if (bt && ["clinic", "education"].includes(businessType))
    signals.push({
      type: "neutral",
      text: `ℹ️ ${bt.label} services are generally ${bt.rates} — tax charges may not always apply`,
    });

  // NTN: now purely database-backed
  if (ntnLookup?.found) {
    score += 40;
    const r = ntnLookup.record;
    signals.push({
      type: "positive",
      text: `✅ NTN found in FBR Active Taxpayer List — ${r.bn || r.nm}`,
    });
    if (ntnLookup.wasSntn)
      signals.push({
        type: "neutral",
        text: `ℹ️ SNTN detected — stripped S prefix to search FBR (${ntnLookup.raw.toUpperCase()} → ${ntnLookup.clean})`,
      });
    if (ntnLookup.checkMismatch)
      signals.push({
        type: "warning",
        text: `⚠️ Check digit on receipt (-${ntnLookup.enteredCheck}) differs from FBR record (-${ntnLookup.storedCheck}) — likely a printing error`,
      });
  } else if (ntnLookup && !ntnLookup.found && ntnLookup.clean) {
    score -= 20;
    signals.push({
      type: "negative",
      text: `❌ NTN ${ntnLookup.clean} not found in FBR Active Taxpayer List`,
    });
  }

  if (strnResult?.valid) {
    score += 25;
    signals.push({ type: "positive", text: `✅ ${strnResult.reason}` });
  } else if (strnResult && !strnResult.valid && strnResult.reason) {
    score -= 10;
    signals.push({ type: "warning", text: `⚠️ ${strnResult.reason}` });
  }

  if (qrAnalysis?.isFBRPOS) {
    score += 35;
    signals.push({
      type: "positive",
      text: "✅ FBR-issued POS QR code detected — bill from registered POS machine",
    });
    if (qrAnalysis.posInvoiceNum)
      signals.push({
        type: "positive",
        text: `✅ POS Invoice #${qrAnalysis.posInvoiceNum} — verify directly on FBR`,
      });
  } else if (qrAnalysis?.isProvincial) {
    score += 30;
    signals.push({
      type: "positive",
      text: "✅ Provincial revenue authority QR code detected",
    });
  } else if (hasQR) {
    score += 5;
    signals.push({
      type: "neutral",
      text: "ℹ️ QR code found but not from a recognised tax authority",
    });
  }

  if (!hasQR && hasTaxClaimed)
    signals.push({
      type: "warning",
      text: "⚠️ Tax charged but no FBR/provincial POS QR code on bill",
    });

  if (ocrNTNLookup) {
    if (ocrNTNLookup.found) {
      score += 15;
      signals.push({
        type: "positive",
        text: `✅ NTN extracted from bill — confirmed in FBR database`,
      });
    } else {
      score -= 15;
      signals.push({
        type: "negative",
        text: `❌ NTN-like number found but not in FBR Active Taxpayer List`,
      });
    }
  }

  if (
    !ntnLookup?.found &&
    !strnResult?.valid &&
    !qrAnalysis?.isFBRPOS &&
    hasTaxClaimed
  )
    signals.push({
      type: "warning",
      text: "⚠️ Tax appears on bill but no verifiable registration signals found",
    });

  const clamped = Math.max(0, Math.min(100, score));
  let status, label;
  if (clamped >= 65) {
    status = "registered";
    label = "status_registered";
  } else if (clamped >= 35) {
    status = "partial";
    label = "status_partial";
  } else if (clamped > 0) {
    status = "weak";
    label = "status_weak";
  } else {
    status = "not_found";
    label = "status_not_found";
  }

  return { score: clamped, status, label, signals };
}
